"use client";

import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { ArrowLeft, Save, Loader2, Plus, X, MessageSquare } from "lucide-react";
import { ReviewComment } from "../../shared/ReviewComment";
import { Label } from "../../ui/label";
import { DocumentManager } from "../../planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { getFY_YEAR } from "@/utils/sharepoint-env";

interface BorrSubstantiveProps {
  onBack?: () => void;
  setActiveSection?: (section: string) => void;
  procedureId?: string;
}

interface Procedure {
  name: string;
  sectionName: string;
}

interface ProcedureResponse {
  id: string;
  textResponse?: { html: string; plainText: string } | null;
  documents?: Array<{ id: string; type: string; name: string; content?: string; url?: string }>;
}

const procedures: Procedure[] = [
  { name: "Repayment testing", sectionName: "RepaymentTesting" },
  { name: "Covenant testing", sectionName: "CovenantTesting" },
  { name: "Drawing Power", sectionName: "DrawingPower" },
  { name: "Agreement Summary", sectionName: "AgreementSummary" },
  { name: "Quarterly Stock Statement", sectionName: "QuarterlyStockStatement" },
  { name: "Classification testing", sectionName: "ClassificationTesting" },
];

// Map procedureId to sectionName
const getSectionName = (procedureId?: string): string | null => {
  if (!procedureId) return null;
  const mapping: Record<string, string> = {
    "repayment-testing": "RepaymentTesting",
    "covenant-testing": "CovenantTesting",
    "drawing-power": "DrawingPower",
    "agreement-summary": "AgreementSummary",
    "quarterly-stock-statement": "QuarterlyStockStatement",
    "classification-testing": "ClassificationTesting",
  };
  return mapping[procedureId] || null;
};

export default function BorrSubstantive({ onBack, setActiveSection, procedureId }: BorrSubstantiveProps) {
  const [companyName, setCompanyName] = useState<string>("");
  const [areaPrefix, setAreaPrefix] = useState<string>("Bor");
  const [responses, setResponses] = useState<ProcedureResponse[]>([]);
  const [inferenceQuestion, setInferenceQuestion] = useState<ProcedureResponse>({
    id: "inference-conclusion",
    textResponse: null,
    documents: [],
  });
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `BORROWINGS_${normalizedSection}`;
  };

  const currentSectionName = getSectionName(procedureId);

  const handleBack = () => {
    if (setActiveSection) {
      setActiveSection("bor-substantive");
    } else if (onBack) {
      onBack();
    }
  };

  // If no procedureId or invalid procedureId, show nothing (should be handled by routing)
  if (!procedureId || !currentSectionName) {
    return null;
  }

  // Find the procedure for the current section
  const currentProcedure = procedures.find(p => p.sectionName === currentSectionName);
  if (!currentProcedure) {
    return null;
  }

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn("Failed to load company name, using fallback");
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load area prefix from Sections.json
  useEffect(() => {
    const loadAreaPrefix = async () => {
      try {
        if ((window as any).sharePointAPI?.downloadSectionsJson) {
          const result = await (window as any).sharePointAPI.downloadSectionsJson();
          if (result?.success && result?.data) {
            const sectionsData = result.data;
            if (sectionsData.Execution && sectionsData.Execution["Borrowings"]) {
              setAreaPrefix(sectionsData.Execution["Borrowings"]);
            }
          }
        }
      } catch (error) {
        console.warn("Failed to load area prefix from Sections.json, using default Bor:", error);
      }
    };
    loadAreaPrefix();
  }, []);

  // Load saved responses
  useEffect(() => {
    const loadSavedResponses = async () => {
      if (!companyName || !areaPrefix) {
        setLoading(false);
        return;
      }

      try {
        const fileName = `Execution_${areaPrefix}_Responses.json`;
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);

        if (result?.success && result?.data?.substantiveProcedures) {
          const savedData = result.data.substantiveProcedures[currentProcedure.name];
          if (savedData && Array.isArray(savedData.responses)) {
            setResponses(savedData.responses.map((r: any, index: number) => ({
              id: r.id || `response-${index}`,
              textResponse: r.textResponse || null,
              documents: (r.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: 'linked',
                name: docName,
              })),
            })));
          } else if (savedData) {
            // Legacy format - single response
            setResponses([{
              id: "response-0",
              textResponse: savedData.textResponse || null,
              documents: (savedData.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: 'linked',
                name: docName,
              })),
            }]);
          }
          
          // Load inference question
          const inferenceQuestionId = `${currentProcedure.sectionName}-INFERENCE`;
          const inferenceData = result.data.substantiveProcedures[inferenceQuestionId];
          if (inferenceData) {
            setInferenceQuestion({
              id: "inference-conclusion",
              textResponse: inferenceData.textResponse || null,
              documents: (inferenceData.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: 'linked',
                name: docName,
              })),
            });
          }
        }
      } catch (error) {
        console.error("Failed to load saved responses:", error);
      } finally {
        setLoading(false);
      }
    };

    loadSavedResponses();
  }, [companyName, areaPrefix, currentProcedure.name]);

  const handleAddResponse = () => {
    const newResponse: ProcedureResponse = {
      id: `response-${Date.now()}`,
      textResponse: null,
      documents: [],
    };
    setResponses([...responses, newResponse]);
  };

  const handleRemoveResponse = (id: string) => {
    setResponses(responses.filter(r => r.id !== id));
  };

  const handleTextResponseChange = (id: string, textResponse: { html: string; plainText: string } | null) => {
    setResponses(responses.map(r => 
      r.id === id ? { ...r, textResponse } : r
    ));
  };

  const handleDocumentsChange = (id: string, documents: Array<{ id: string; type: string; name: string; content?: string; url?: string }>) => {
    setResponses(responses.map(r => 
      r.id === id ? { ...r, documents } : r
    ));
  };

  const handleSave = useCallback(async () => {
    if (!companyName || !areaPrefix) {
      setSaveError("Missing required data (company name or area prefix)");
      setSaveStatus('error');
      return;
    }

    // Auto-save any pending textbox content before processing responses
    const documentManagers = document.querySelectorAll('[data-document-manager]');
    for (const manager of Array.from(documentManagers)) {
      const itemId = manager.getAttribute('data-item-id');
      if (itemId) {
        const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
        if (autoSaveMethod && typeof autoSaveMethod === 'function') {
          autoSaveMethod();
        }
      }
    }

    // Wait for auto-save to complete
    await new Promise(resolve => setTimeout(resolve, 1000));

    setSaving(true);
    setSaveStatus('saving');
    setSaveError(null);

    try {
      const fileName = `Execution_${areaPrefix}_Responses.json`;

      // Load existing file first
      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch (error) {
        console.warn(`Failed to download existing ${fileName}, starting from empty`, error);
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Auto-save inference question textbox
      const inferenceItemId = `${currentProcedure.sectionName}-INFERENCE`;
      const inferenceAutoSave = (window as any)[`autoSaveTextbox_${inferenceItemId}`];
      if (inferenceAutoSave && typeof inferenceAutoSave === 'function') {
        inferenceAutoSave();
      }
      await new Promise(resolve => setTimeout(resolve, 500));

      // Save the responses
      const responsesToSave = responses.map(r => ({
        id: r.id,
        textResponse: r.textResponse || null,
        documents: (r.documents || []).map((d) => d.name || d),
      }));

      existingData.substantiveProcedures[currentProcedure.name] = {
        responses: responsesToSave,
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      // Save inference question - query db.json for section field like CustomProcedure
      const inferenceQuestionId = `${currentProcedure.sectionName}-INFERENCE`;
      const sectionToFind = `Execution_${areaPrefix}_${inferenceQuestionId}`;
      let inferenceDocumentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            inferenceDocumentNames = dbResult.data
              .map((file: any) => file.name || '')
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch (error) {
        console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
      }

      const inferenceTextboxFromLocalStorage = localStorage.getItem(`textbox_${inferenceItemId}`);
      const hasInferenceTextboxContent = inferenceTextboxFromLocalStorage && inferenceTextboxFromLocalStorage.trim().length > 0;
      
      let inferenceTextResponse = inferenceQuestion.textResponse || null;
      if (hasInferenceTextboxContent) {
        inferenceTextResponse = {
          html: inferenceTextboxFromLocalStorage,
          plainText: inferenceTextboxFromLocalStorage.replace(/<[^>]*>/g, '')
        };
      }

      existingData.substantiveProcedures[inferenceQuestionId] = {
        selectedOption: "",
        textResponse: inferenceTextResponse,
        documents: inferenceDocumentNames,
        table: null,
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      // Upload to SharePoint
      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `Execution ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: getFY_YEAR(),
        sectionList: [`Execution_${areaPrefix}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save responses");
      }

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error("Failed to save responses:", error);
      setSaveStatus('error');
      setSaveError(error instanceof Error ? error.message : "Failed to save responses");
    } finally {
      setSaving(false);
    }
  }, [companyName, areaPrefix, responses, currentProcedure.name, inferenceQuestion, currentProcedure.sectionName]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-white/60">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">{currentProcedure.name}</h2>
          <p className="text-gray-400">Document procedures for this substantive procedure</p>
        </div>
        <Button variant="outline" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">{currentProcedure.name}</CardTitle>
            <Button
              onClick={handleAddResponse}
              variant="outline"
              size="sm"
              className="border-white/20 text-white hover:bg-white/10"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {responses.length === 0 ? (
            <div className="text-center py-8 text-white/60">
              <p>No responses yet. Click "Add" to start documenting this procedure.</p>
            </div>
          ) : (
            responses.map((response, index) => (
              <Card key={response.id} className="border-white/10 bg-black/20 mb-4">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium text-white">
                      {currentProcedure.name} {responses.length > 1 ? `(${index + 1})` : ''}
                    </Label>
                    {responses.length > 1 && (
                      <Button
                        onClick={() => handleRemoveResponse(response.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <DocumentManager
                    sectionKey={`execution-substantive:Borrowings`}
                    itemId={`${currentProcedure.sectionName}-${response.id}`}
                    itemQuestion={currentProcedure.name}
                    itemTextResponse={response.textResponse || null}
                    itemDocuments={response.documents || []}
                    onDocumentsChange={(docs) => handleDocumentsChange(response.id, docs)}
                    onTextResponseChange={(textResp) => handleTextResponseChange(response.id, textResp)}
                    disabled={false}
                  />
                </CardContent>
              </Card>
            ))
          )}
        </CardContent>
      </Card>

      {/* Inference and Conclusion Question */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="text-white">Document the inference and conclusion from the above procedure performed.</CardTitle>
        </CardHeader>
        <CardContent>
          <DocumentManager
            sectionKey={`execution-substantive:Borrowings`}
            itemId={`${currentProcedure.sectionName}-INFERENCE`}
            itemQuestion="Document the inference and conclusion from the above procedure performed."
            itemTextResponse={inferenceQuestion.textResponse || null}
            itemDocuments={inferenceQuestion.documents || []}
            onDocumentsChange={(docs) => setInferenceQuestion({ ...inferenceQuestion, documents: docs })}
            onTextResponseChange={(textResp) => setInferenceQuestion({ ...inferenceQuestion, textResponse: textResp })}
            disabled={false}
          />
        </CardContent>
      </Card>

      <div className="flex justify-end items-center gap-4 pt-6">
        {saveError && (
          <div className="text-red-400 text-sm mr-auto">{saveError}</div>
        )}
        {saveStatus === 'saved' && (
          <div className="text-green-400 text-sm mr-auto">Saved successfully!</div>
        )}
        <Button
          onClick={handleSave}
          disabled={saving || saveStatus === 'saving'}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          {saving || saveStatus === 'saving' ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : saveStatus === 'saved' ? (
            <>
              <Save className="h-4 w-4 mr-2" />
              Saved
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save
            </>
          )}
        </Button>
      </div>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {};
            procedures.forEach((proc) => {
              sectionMap[getSectionQuestionId(proc.name)] = proc.name;
            });
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}
